<script lang="ts">
  import { onMount, onDestroy } from 'svelte'
  import { demoTracker } from '$lib/stores/demoStore'
  import { showPricingModal } from '$lib/stores/subscription'

  let isOpen = $state(false)
  let feature = $state<'spellCheck' | 'ai'>('spellCheck')
  let selectedOption = $state<'download' | 'web' | 'subscribe' | null>(null)
  let hasExtended = $state(false)
  let isUsageExpired = $state(false) // Track if usage is actually expired
  let isRateLimited = $state(false) // Track if triggered by rate limit (429)

  const options = [
    {
      id: 'download' as const,
      icon: '🖥',
      title: 'Mac 앱 다운로드',
      subtitle: '가장 빠르고 강력한 경험',
      badge: '추천',
      color: 'primary'
    },
    {
      id: 'subscribe' as const,
      icon: '⭐',
      title: '바로 구독하기',
      subtitle: '웹 버전 무제한 사용',
      color: 'premium'
    },
    {
      id: 'web' as const,
      icon: '🌐',
      title: '웹 버전 사용해보기',
      subtitle: '브라우저에서 바로 시작',
      color: 'secondary'
    }
  ]

  function handleDemoExpired(event: CustomEvent) {
    feature = event.detail?.feature || 'spellCheck'
    hasExtended = demoTracker.getState().hasExtended
    isRateLimited = event.detail?.isRateLimited || false

    // Check if usage is actually expired
    const remaining = demoTracker.getRemaining()
    if (feature === 'spellCheck') {
      // If rate limited (429 error), force showing as expired
      isUsageExpired = isRateLimited || remaining.spellCheck === 0
    } else {
      isUsageExpired = isRateLimited || remaining.ai === 0
    }

    isOpen = true
  }

  function handleClose() {
    isOpen = false
    selectedOption = null
  }

  function handleOptionClick(optionId: 'download' | 'web' | 'subscribe') {
    selectedOption = optionId

    switch (optionId) {
      case 'download':
        // Direct download DMG file
        window.location.href = 'https://github.com/hyungyunlim/korean-grammar-checker/releases/latest/download/korean-grammar-checker.dmg'
        handleClose()
        break
      case 'subscribe':
        // Show pricing modal directly
        handleClose()
        showPricingModal.set(true)
        break
      case 'web':
        // Open web app in new tab
        window.open('https://app.korean-grammar.com', '_blank')
        handleClose()
        break
    }
  }

  function handleExtendDemo() {
    if (hasExtended) {
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: {
          message: '이미 체험 연장을 사용하셨습니다',
          type: 'info'
        }
      }))
      return
    }

    const success = demoTracker.extendDemo(2)
    if (success) {
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: {
          message: '체험이 연장되었습니다! 맞춤법 +2회, AI +2회',
          type: 'success'
        }
      }))
      handleClose()
    }
  }

  onMount(() => {
    window.addEventListener('demo-expired', handleDemoExpired as EventListener)
  })

  onDestroy(() => {
    window.removeEventListener('demo-expired', handleDemoExpired as EventListener)
  })

  // Handle escape key
  $effect(() => {
    function handleKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen) {
        handleClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeydown)
      return () => document.removeEventListener('keydown', handleKeydown)
    }
  })
</script>

{#if isOpen}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="modal-overlay" onclick={handleClose} role="presentation">
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div class="modal-container" onclick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="modal-title" tabindex="-1">
      <div class="modal-header">
        <h2 id="modal-title" class="modal-title">
          {#if isUsageExpired}
            {#if isRateLimited}
              데모 체험이 종료되었습니다
            {:else}
              {feature === 'spellCheck' ? '맞춤법 검사' : 'AI 기능'} 체험이 종료되었습니다
            {/if}
          {:else}
            전체 버전으로 업그레이드하세요
          {/if}
        </h2>
        <button class="close-btn" onclick={handleClose} aria-label="모달 닫기">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <p class="description">
          {#if isUsageExpired}
            {#if isRateLimited}
              데모 체험 횟수가 모두 소진되었습니다. AI 글쓰기의 모든 기능을 무제한으로 사용하시려면:
            {:else}
              AI 글쓰기의 모든 기능을 무제한으로 사용하시려면:
            {/if}
          {:else}
            {#if feature === 'spellCheck'}
              맞춤법 검사 {demoTracker.getRemaining().spellCheck}회 남았습니다.
            {:else}
              AI 기능 {demoTracker.getRemaining().ai}회 남았습니다.
            {/if}
            전체 버전에서는 모든 기능을 무제한으로 사용할 수 있습니다:
          {/if}
        </p>

        <div class="options-container">
          {#each options as option}
            <button
              class="option-card {option.color}"
              class:selected={selectedOption === option.id}
              onclick={() => handleOptionClick(option.id)}
            >
              <div class="option-icon">{option.icon}</div>
              <div class="option-content">
                <h3 class="option-title">{option.title}</h3>
                <p class="option-subtitle">{option.subtitle}</p>
              </div>
              {#if option.badge}
                <span class="option-badge">{option.badge}</span>
              {/if}
            </button>
          {/each}
        </div>

        {#if !hasExtended && !isRateLimited}
          <div class="extend-section">
            <button class="extend-btn" onclick={handleExtendDemo}>
              <svg class="extend-icon" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z" clip-rule="evenodd"/>
              </svg>
              조금 더 체험하기 (+2회 제공)
            </button>
          </div>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    animation: fadeIn 0.2s ease;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .modal-container {
    background: white;
    border-radius: 16px;
    max-width: 560px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    animation: slideUp 0.3s ease;
  }

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px;
    border-bottom: 1px solid #e5e7eb;
  }

  .modal-title {
    font-size: 20px;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .close-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: 8px;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .close-btn:hover {
    background: #f3f4f6;
    color: #111827;
  }

  .modal-body {
    padding: 24px;
  }

  .description {
    color: #4b5563;
    margin-bottom: 24px;
  }

  .options-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 20px;
  }

  .option-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    background: #f9fafb;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    text-align: left;
    width: 100%;
  }

  .option-card:hover {
    border-color: #3b82f6;
    background: #eff6ff;
    transform: translateX(4px);
  }

  .option-card.selected {
    border-color: #3b82f6;
    background: #eff6ff;
  }

  .option-card.primary:hover {
    border-color: #3b82f6;
  }

  .option-card.premium:hover {
    border-color: #f59e0b;
    background: #fffbeb;
  }

  .option-card.secondary:hover {
    border-color: #10b981;
  }

  .option-icon {
    font-size: 32px;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .option-content {
    flex: 1;
  }

  .option-title {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 4px 0;
  }

  .option-subtitle {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
  }

  .option-badge {
    position: absolute;
    top: 8px;
    right: 12px;
    background: #3b82f6;
    color: white;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 12px;
  }

  .extend-section {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
  }

  .extend-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 12px;
    background: transparent;
    color: #6b7280;
    border: 1px dashed #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .extend-btn:hover {
    background: #f9fafb;
    color: #3b82f6;
    border-color: #3b82f6;
  }

  .extend-icon {
    width: 16px;
    height: 16px;
  }

  /* Dark mode */
  :global(.dark) .modal-container {
    background: #1f2937;
  }

  :global(.dark) .modal-header {
    border-bottom-color: #374151;
  }

  :global(.dark) .modal-title {
    color: #f9fafb;
  }

  :global(.dark) .close-btn {
    color: #9ca3af;
  }

  :global(.dark) .close-btn:hover {
    background: #374151;
    color: #f9fafb;
  }

  :global(.dark) .description {
    color: #d1d5db;
  }

  :global(.dark) .option-card {
    background: #374151;
    border-color: #4b5563;
  }

  :global(.dark) .option-card:hover {
    background: #1e3a5f;
  }

  :global(.dark) .option-title {
    color: #f9fafb;
  }

  :global(.dark) .option-subtitle {
    color: #9ca3af;
  }

  :global(.dark) .extend-btn {
    color: #9ca3af;
    border-color: #4b5563;
  }

  :global(.dark) .extend-btn:hover {
    background: #374151;
    color: #3b82f6;
  }

  /* Mobile responsiveness */
  @media (max-width: 640px) {
    .modal-container {
      width: 95%;
      max-height: 90vh;
    }

    .option-icon {
      font-size: 24px;
      width: 36px;
      height: 36px;
    }

    .option-title {
      font-size: 14px;
    }

    .option-subtitle {
      font-size: 12px;
    }
  }
</style>